import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogAnimationService } from '../../services/dialog-animation.service';
import { ListValueSelectorComponent, ValueOption } from '../list-value-selector/list-value-selector.component';
import { IconStatus } from '../../interfaces/list-item.interfaces';
import { getTranslationKeyForValue } from '../../utils/functions/translation.functions';
import { SliderComponent } from '../slider/slider.component';
import { Observable } from 'rxjs';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY_FACTORY_PROVIDER } from '@angular/material/autocomplete';
import { MAT_SELECT_SCROLL_STRATEGY_PROVIDER } from '@angular/material/select';

export enum InputType
{
  /**
   * Basic text input
   */
  Text = 'text',
  /**
   * Local timezone datetime
   */
  DatetimeLocal = "datetime-local",
  /**
   * Month of a year
   */
  Month = 'month',
  /**
   * Week of a year
   */
  Week = 'week',
  /**
   * Search field
   */
  Search = 'search',
  /**
   * Color picker
   */
  Color = 'color',
  /**
   * Text input for an email address
   */
  Email = 'email',
  /**
   * Input for a telephone number
   */
  Tel = 'tel',
  /**
   * Password input that displays *
   */
  Password = 'password',
  /**
   * Boolean switch
   */
  Switch = 'switch',
  /**
   * Numeric slider
   */
  Slider = 'slider',
  /**
   * Numeric text field
   */
  Number = 'number',
  /**
   * Date selection
   */
  Date = 'date',
  /**
   * 24 hour time selection
   */
  Time = 'time',
  /**
   * Read only text with a chevron indicating that a menu will be opened when clicked. The menu is handled by this component, for a custom click handler
   * use the Menu type instead.
   */
  Options = 'options',
  /**
   * Read only text with a chevron indicating that a menu will be opened when clicked. Parent component is responsible for handling the click event.
   */
  Menu = 'menu'
}

export type InputValue = string | number | boolean | string[];

export class InputChange {
  source: InputComponent;
  value: InputValue;
}

export interface OptionInfo {
  /**
   * Actual value of this option that is stored in the control.
   */
  value: string;
  /**
   * Optional display text that is shown instead of value.
   */
  displayText?: string;
  /**
   * Optional disabled flag to disallow selecting the option.
   */
  disabled?: boolean;
}

// Any 'options' type inputs that have at most this many options will be automatically shown as a combo box
const AUTO_COMBOBOX_THRESHOLD: number = 0;

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    },
    // Required so that the component works in dialogs
    // see https://github.com/angular/angular/issues/35759#issuecomment-1044888476
    MAT_AUTOCOMPLETE_SCROLL_STRATEGY_FACTORY_PROVIDER,
    MAT_SELECT_SCROLL_STRATEGY_PROVIDER,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements ControlValueAccessor {
  private static readonly INPUT_IS_CHANGED_TYPES: InputType[] = [InputType.Text, InputType.Password, InputType.Email, InputType.Tel, InputType.Number];
  private static readonly INPUT_IS_NUMERIC_TYPES: InputType[] = [InputType.Slider, InputType.Number];
  private static readonly INPUT_IS_BOOLEAN_TYPES: InputType[] = [InputType.Switch];

  @ViewChild('child') private slider: SliderComponent;
  @ViewChild('container') private container: ElementRef<Element> = undefined;

  // General settings
  @Input() public formControlName: string = undefined;
  @Input() public label: string = '';
  @Input() public type: InputType = InputType.Text;
  @Input() public disabled: boolean = false;
  @Input() public info: string = '';
  @Input() public min: number = undefined;    // Setting them to undefined ensures unlimited range if min and max are not specified
  @Input() public max: number = undefined;
  @Input() public minLength: number = 0;      // Using undefined doesn't work (it seems to act as 0), so just use large values
  @Input() public maxLength: number = 1000000000;
  @Input() public suffixIcon: string = '';
  @Input() public minDate: string = '';
  @Input() public maxDate: string = '';

  // Text specific settings
  @Input() public autoCompleteOptions$: Observable<string[]> = undefined;

  // Slider specific settings
  @Input() public leftIcon: IconStatus = { class: '', icon: 'remove' };
  @Input() public rightIcon: IconStatus = { class: '', icon: 'add' };
  @Input() public step: number = 1;
  @Input() public logarithmic: boolean = false;

  // Combobox settings
  private _useComboBox : boolean;
  public get useComboBox() : boolean {
    // The combo box cannot be disabled (bug), so don't show it if this input is disabled
    return !this.disabled && (this._useComboBox || (
          this.basicOptions.length <= AUTO_COMBOBOX_THRESHOLD &&
          this.keyValueOptions.length <= AUTO_COMBOBOX_THRESHOLD &&
          this.options.length <= AUTO_COMBOBOX_THRESHOLD));
  }
  @Input()
  public set useComboBox(v : boolean) {
    this._useComboBox = v;
  }

  // Options specific settings
  /**
   * Basic string options. When basicTranslateKey is empty they are both the value of the control and what is displayed to the user.
   */
  @Input() public basicOptions: string[] = [];
  /**
   * Translate key used to optionally translate the display values of basicOptions automatically. The control value will still be
   * the values in the basicOptions array, but they are displayed in a translated way. Actual translation key is:
   * basicTranslateKey + '.' + firstCharToUpper(basicOptions[i])
   */
  @Input() public basicTranslateKey: string = '';
  /**
   * Options for a menu or dropdown. Various settings per option may be specified, see OptionInfo docs for specifics.
   */
  @Input() public options: OptionInfo[] = [];
  /**
   * Key-value options. The keys are the value of the control and the values are what is displayed to the user.
   * This provides a way to override the automatic translation provided by the basicOptions.
   * @deprecated This should be replaced with "options" where possible.
   */
  @Input() public keyValueOptions: [string, string][] = [];
  /**
   * Header string to show in options selector. Will default to the value of the label property if not set.
   */
  @Input() public optionsHeader: string = '';
  /**
   * Info text for the options selector displayed below the header.
   */
  @Input() public optionsInfo: string = '';
  /**
   * Treat the input value as a number, even though normal rules would suggest it is something else
   */
  @Input() public treatAsNumber: boolean = false;
  /**
   * Treat the input value as a string array.
   */
  @Input() public treatAsStringArray: boolean = false;

  @Output() readonly change: EventEmitter<InputChange> = new EventEmitter<InputChange>();
  @Output() readonly input: EventEmitter<InputChange> = new EventEmitter<InputChange>();
  @Output() readonly suffixClick: EventEmitter<void> = new EventEmitter<void>();

  public value: InputValue = null;
  public isFocused: boolean = false;
  public showPassWord: boolean = false;
  public passwordType: string = "password";

  private onTouched: any = null;
  private onChange: any = null;
  private touched: boolean = false;

  constructor(
    private ngZone: NgZone,
    public dialog: DialogAnimationService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    ) {
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public writeValue(value: any): void {
    if (value != null) {
      this.value = this.castValue(value);

      if (this.type === InputType.Slider && this.slider) {
        this.slider.writeValue(value);
      }

      this.cdr.markForCheck();
    }
  }

  public setDisabledState(isDisabled: boolean): void {
    if (this.disabled !== isDisabled) {
      this.disabled = isDisabled;
      this.cdr.markForCheck();
    }
  }

  public showEmptyButton(): boolean {
    return String(this.value).length > 0 && !this.disabled && this.isFocused;
  }

  public emptyValue(): void {
    this.onChildChange('');
  }

  public showToggleVisibilityButton(): boolean {
    return (this.value !== null) && (this.value !== undefined) && (this.value !== '');
  }

  public toggleVisibilityPassword(): void {
    if (this.passwordType === 'password') {
      this.passwordType = 'text';
      this.showPassWord = true;
    } else {
      this.passwordType = 'password';
      this.showPassWord = false;
    }
  }

  public async onFocus(): Promise<void> {
    if (this.isFocused) {
      await new Promise(f => setTimeout(f, 200));
    }
    this.isFocused = !this.isFocused;
    this.cdr.markForCheck();
  }

  public onChildInput(value: InputValue): void {

    if(this.type === InputType.Number)
    {
      if(value as any === "" ){
        return;
      }
    }

    this.value = this.castValue(value);
    this.markAsTouched();
    this.input.emit(this.createChangeEvent());

    if (InputComponent.INPUT_IS_CHANGED_TYPES.includes(this.type)) {
      // Also fire an onChange event for quicker form updating
      this.onChildChange(this.value);
    }
  }

  public getResolvedOptions(): OptionInfo[] {
    if (this.basicOptions.length > 0) {
      return this.basicOptions.map(bo => ({
        value: bo,
        displayText: this.getOptionDisplayValue(bo),
      }));
    }
    if (this.options.length > 0) {
      return this.options;
    }
    return this.keyValueOptions.map(kv => ({
      value: kv[0],
      displayText: kv[1],
    }));
  }

  public onChildInputComboBox(value: InputValue): void {

    if (this.value !== value) {
      this.value = this.castValue(value);
      this.markAsTouched();
      this.input.emit(this.createChangeEvent());
      this.onChildChange(this.value);
    }

  }

  public onChildChange(value: InputValue): void {
    this.value = this.castValue(value);
    if ((this.type === InputType.Menu || this.type === InputType.Options) && !this.useComboBox) {
      const inputElement: HTMLInputElement = this.container.nativeElement.querySelector('input');
      if (inputElement) {
        // Move back to the start of the text
        inputElement.setSelectionRange(0, 0);
        inputElement.focus();
      }
      else {
        console.error(`Could not find input element on <app-input> for '${this.formControlName}'`);
      }
    }
    if (this.onChange) {
      this.onChange(this.value);
    }
    this.change.emit(this.createChangeEvent());
  }

  public openOptionsDialog(): void {
    if (this.disabled) {
      return;
    }

    const options: ValueOption[] = [];

    // The ListValueSelectorComponent does not support disabled entries at the moment (it doesn't need to for our usecases)
    // so just ignore the disabled options in the list.
    this.getResolvedOptions().filter(o => !o.disabled).forEach((option) => options.push({ value: option.value, displayText: option.displayText ?? option.value }));

    const selectedValue: string = this.value.toString();
    const header: string = this.optionsHeader.length > 0 ? this.optionsHeader : this.label;
    const headerInfo: string = this.optionsInfo;

    const dialogRef = this.dialog.open(ListValueSelectorComponent, {
      panelClass: 'my-full-screen-dialog',
      data: {
        options: options,
        header: header,
        headerInfo: headerInfo,
        selectedValue: selectedValue
      },
      animation: { to: "left" }
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result === undefined) {
        return;
      }
      window.setTimeout(() => {
        this.ngZone.run(() => {
          if (selectedValue !== result) {
            this.onChildChange(result);
            this.cdr.markForCheck();
          }
        }, 100);
      });
    });
  }

  public getOptionDisplayValue(value: string | number): string {
    if (value === undefined || value === null) {
      // Prevent undefined or null from being translated
      return value as string;
    }

    // Device properties with options use actual numbers as their values, so we have to convert to string explicitly
    const valueAsString: string = value.toString();

    // In case the string is empty we should show nothing
    if (valueAsString.trim().length === 0) {
      return valueAsString;
    }

    if (this.basicOptions.length > 0) {
      if (!this.basicTranslateKey || this.basicTranslateKey.length === 0) {
        // Using basic options, but we have no translate key. Display the value itself.
        return valueAsString;
      }

      // We do have a translate key, return the translation
      return this.translate.instant(`${this.basicTranslateKey}.${getTranslationKeyForValue(valueAsString)}`);
    }
    else if (this.options.length > 0) {
      const option = this.options.find(o => o.value === valueAsString);
      return option?.displayText ?? valueAsString;
    }

    // Use the display value stored in keyValueOptions
    const option: [string, string] = this.keyValueOptions.find((option: [string, string]) => option[0] === valueAsString);
    if (option === undefined) {
      return valueAsString;
    }
    return option[1];
  }

  public onSuffixIconClicked(event: Event): void {
    event.stopPropagation();
    this.suffixClick.emit();
  }

  private castValue(value: InputValue): InputValue {
    if (InputComponent.INPUT_IS_NUMERIC_TYPES.includes(this.type)) {
      return Number(value);
    }
    else if (InputComponent.INPUT_IS_BOOLEAN_TYPES.includes(this.type)) {
      return Boolean(value);
    }
    else if (this.treatAsStringArray) {
      return value as string[];
    }
    else if (this.treatAsNumber) {
      return Number(value);
    }
    return String(value);
  }

  private markAsTouched(): void {
    if (!this.touched) {
      if (this.onTouched) {
        this.onTouched();
      }
      this.touched = true;
    }
  }

  private createChangeEvent(): InputChange {
    return {
      source: this,
      value: this.value
    };
  }
}
